import { useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

export default function PracticeAreaDetails({ onBookingOpen }) {
  const [expanded, setExpanded] = useState({});
  const { t } = useLanguage();
  const pd = t("practiceDetails");
  const items = t("practiceAreas.items") || [];
  const attorneys = t("attorneys.items") || [];

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <section data-testid="practice-area-details-section" className="py-24 md:py-32 bg-[#f5f0e8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <Reveal className="mb-16">
          <p className="text-[#1a5c4a] text-xs uppercase tracking-[0.3em] mb-3 font-semibold">{pd.subtitle}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a1a2e] mb-4 tracking-[-0.02em]">{pd.headline}</h2>
        </Reveal>

        <div className="flex flex-col gap-5">
          {items.map((area) => {
            const isOpen = expanded[area.id];
            const attorney = attorneys.find((a) => a.name === area.attorney);
            return (
              <Reveal key={area.id}>
                <div
                  id={`detail-${area.id}`}
                  data-testid={`practice-detail-${area.id}`}
                  className="bg-white rounded-2xl overflow-hidden transition-all duration-300 shadow-[0_2px_12px_rgba(0,0,0,0.04)] border border-[#e8e3db]"
                >
                  <button
                    data-testid={`practice-detail-toggle-${area.id}`}
                    onClick={() => toggle(area.id)}
                    className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-[#faf8f4] transition-colors"
                  >
                    <div>
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-[#1a1a2e]">{area.title}</h3>
                      <p className="text-[#4a4540] text-sm mt-1 hidden sm:block">{area.shortDesc}</p>
                    </div>
                    {isOpen ? (
                      <ChevronUp className="h-5 w-5 text-[#1a5c4a] shrink-0 ml-4" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-[#4a4540] shrink-0 ml-4" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-6 md:px-8 pb-8 border-t border-[#f0ebe3]">
                      <div className="grid md:grid-cols-3 gap-8 pt-6">
                        <div className="md:col-span-2 space-y-6">
                          <p className="text-[#4a4540] leading-relaxed">{area.fullDesc}</p>
                          <div>
                            <h4 className="font-heading text-lg font-bold text-[#1a1a2e] mb-3">{pd.commonCases}</h4>
                            <div className="flex flex-wrap gap-2">
                              {area.cases.map((c) => (
                                <Badge key={c} variant="secondary" className="bg-[#f0ebe3] text-[#1a1a2e] border-0 rounded-full px-4 py-1.5 text-xs font-medium">
                                  {c}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-heading text-lg font-bold text-[#1a1a2e] mb-3">{pd.faqTitle}</h4>
                            <Accordion type="single" collapsible className="w-full">
                              {area.faq.map((item, i) => (
                                <AccordionItem key={`${area.id}-faq-${i}`} value={`faq-${area.id}-${i}`} className="border-[#f0ebe3]">
                                  <AccordionTrigger className="text-left text-[#1a1a2e] hover:text-[#1a5c4a] hover:no-underline font-medium">
                                    {item.q}
                                  </AccordionTrigger>
                                  <AccordionContent className="text-[#4a4540]">{item.a}</AccordionContent>
                                </AccordionItem>
                              ))}
                            </Accordion>
                          </div>
                        </div>

                        <div className="space-y-6">
                          {attorney && (
                            <div className="bg-[#faf8f4] rounded-2xl p-6">
                              <img src={attorney.photo} alt={attorney.name} className="w-full h-48 object-cover rounded-xl mb-4" />
                              <h4 className="font-heading text-lg font-bold text-[#1a1a2e]">{attorney.name}</h4>
                              <p className="text-sm text-[#4a4540]">{attorney.title}</p>
                              <p className="text-xs text-[#1a5c4a] mt-1 font-medium">{attorney.yearsExperience}+ {pd.yearsExp}</p>
                            </div>
                          )}
                          <Button
                            data-testid={`get-help-${area.id}`}
                            onClick={() => onBookingOpen(area.title)}
                            className="w-full bg-[#1a5c4a] text-white hover:bg-[#154a3c] rounded-full px-6 py-5 font-semibold transition-all duration-300 hover:-translate-y-0.5"
                          >
                            {pd.getHelp} <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
